/**
 * Common date formatting utilities for the application
 */

/**
 * Format a timestamp to show date and time with timezone
 * @param isoString - ISO string timestamp (should be in UTC)
 * @returns Formatted string like "Jan 15, 2024, 5:30:00 AM EST"
 */
export const formatTimestamp = (isoString: string): string => {
  try {
    let date: Date;
    let cleanTimestamp = isoString;
    
    // Handle malformed timestamps that have both +00:00 and Z
    if (cleanTimestamp.includes('+00:00Z')) {
      cleanTimestamp = cleanTimestamp.replace('+00:00Z', 'Z');
    }
    
    // If the timestamp doesn't have timezone info, treat it as UTC
    if (!cleanTimestamp.endsWith('Z') && !cleanTimestamp.includes('+') && !cleanTimestamp.includes('-', 10)) {
      // Add Z to indicate UTC
      date = new Date(cleanTimestamp + 'Z');
    } else {
      date = new Date(cleanTimestamp);
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', isoString, 'cleaned to:', cleanTimestamp);
      return 'Invalid Date';
    }
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Error';
  }
};

/**
 * Format a date to show only the date (no time)
 * @param isoString - ISO string timestamp (should be in UTC)
 * @returns Formatted string like "Jan 15, 2024"
 */
export const formatDate = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', isoString);
      return 'Invalid Date';
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error';
  }
};

/**
 * Format a timestamp to show date and time without timezone (for file uploads, etc.)
 * @param isoString - ISO string timestamp (should be in UTC)
 * @returns Formatted string like "Jan 15, 2024, 5:30:00 AM"
 */
export const formatDateTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', isoString);
      return 'Invalid Date';
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  } catch (error) {
    console.error('Error formatting date/time:', error);
    return 'Error';
  }
}; 